import { useCallback, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { FormHandles } from '@unform/core'

import Form from '~/components/unform/Form'
import { isPublisher } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import { useAppSelector } from '~/store/hooks'

import { useCanEditBudget } from '../../hooks/useCanEditBudget'
import { EditCost } from './EditCost'
import { useEditBudget } from './useEditBudget'
import { ViewCost } from './View'

type GenericEditCostProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataSchemaTable: any
  type: 'cpc' | 'cpm'
  isEdit?: boolean
  origin?: BidEditOrigin
  initialCostOverride?: number
  onRequestEdit?: () => void
  onCancel?: () => void
}

export const GenericEditCost = ({
  dataSchemaTable,
  type,
  isEdit: isEditProp,
  origin,
  initialCostOverride,
  onRequestEdit,
  onCancel
}: GenericEditCostProps) => {
  const { id } = useParams()
  const { lengthCentsCurrency } = useAppInfo()

  const isControlled = isEditProp !== undefined
  const [internalIsEdit, setInternalIsEdit] = useState(false)
  const isEdit = isControlled ? isEditProp : internalIsEdit

  // Local Loading para não colidir com outros elementos fazendo requisição
  // Ideal seria o loading do Redux aceitar ID do item editado
  const [localLoading, setLocalLoading] = useState(false)

  const formRef = useRef<FormHandles>(null)

  const {
    campaign: {
      item: { data: campaignData }
    },
    industry: {
      budget: {
        minCosts: { data: minCostsData }
      }
    }
  } = useAppSelector(state => state.retailMedia)

  /**
   * Checagem se o ID da URL é igual ao ID do Redux
   * Isso é bug. Ao entrarmos numa campanha e voltarmos para a listagem, os dados da campanha
   * seguem armazenado no redux e o componente entende que estamos dentro da campanha.
   */
  const idIsValid = useMemo(
    () => !!campaignData?.id && campaignData.id === id,
    [campaignData?.id, id]
  )

  /**
   * Campanhas offsite cobradas por investimento total não têm um bid de CPM
   * configurável: o CPM exibido é o efetivo (avg_cpm), sempre somente leitura.
   */
  const isOffsiteTotalBudget = useMemo(
    () =>
      campaignData?.type === 'off_site' &&
      campaignData?.raw?.cost_type === 'total_budget',
    [campaignData]
  )

  const canEdit = useCanEditBudget(type)

  const showMin = useMemo(
    () => campaignData && !!minCostsData && idIsValid,
    [campaignData, idIsValid, minCostsData]
  )

  const itemMinCosts = useMemo(() => {
    const response = minCostsData?.ads?.filter(
      item => item.id === dataSchemaTable.settings.product_id
    )

    return response?.length ? response : [minCostsData.global]
  }, [dataSchemaTable.settings.product_id, minCostsData])

  const values = useMemo(
    () => ({
      adCost: dataSchemaTable?.settings?.[type],
      adCostCampaign: dataSchemaTable?.campaign_settings?.[type],
      campaignCost: campaignData?.settings?.[type],
      globalMinCost: minCostsData?.global?.[`min_${type}`],
      selfMinCost: itemMinCosts?.[0]?.[`min_${type}`]
    }),
    [dataSchemaTable, type, campaignData, minCostsData, itemMinCosts]
  )

  const isValidCost = useMemo(
    () => Number(values.adCost) === Number(values.campaignCost),
    [values.adCost, values.campaignCost]
  )

  const showMinBadge = useMemo(() => {
    // checar se usa custo global da campanha
    const adUsesGlobalCost = !values.adCost || values.adCost === null

    // checar se tem cpc mínimo para o anúncio
    const hasSelfCost =
      !isNaN(Number(values.selfMinCost)) && Number(values.selfMinCost) > -1

    // setar quais valores devemos olhar para comparar
    const currentCostValue = adUsesGlobalCost
      ? values.campaignCost
      : values.adCost
    const currentMinCPCValue = hasSelfCost
      ? values.selfMinCost
      : values.globalMinCost

    return currentMinCPCValue > currentCostValue
  }, [values])

  const requestEdit = useCallback(() => {
    if (isControlled) {
      onRequestEdit?.()
      return
    }

    setInternalIsEdit(true)
  }, [isControlled, onRequestEdit])

  const cancelEdit = useCallback(() => {
    if (isControlled) {
      onCancel?.()
      return
    }

    setInternalIsEdit(false)
  }, [isControlled, onCancel])

  const handleSuccess = useCallback(() => {
    cancelEdit()
  }, [cancelEdit])

  const { handleEditRequest } = useEditBudget({
    type,
    setLocalLoading,
    handleSuccess,
    id: dataSchemaTable?.id
  })

  const handleSubmit = useCallback(
    (data: { budget: { numAsString: string } }) => {
      if (data.budget) {
        const amount = data.budget.numAsString
        const value = lengthCentsCurrency
          ? Number(amount) / 100
          : Number(amount)

        handleEditRequest(value, origin)
      }
    },
    [handleEditRequest, lengthCentsCurrency, origin]
  )

  const costValue = useMemo(() => {
    if (isOffsiteTotalBudget && idIsValid && type === 'cpm') {
      const avgCpm = Number(dataSchemaTable?.metrics?.raw?.avg_cpm)

      // avg_cpm can come back null/'.00' (both format as 0) when there are no
      // impressions; NaN makes formatMoney fall back to '-' instead of R$ 0,00.
      return avgCpm || Number.NaN
    }

    const adUsesGlobalCost = values.adCost == null || values.adCost === ''

    if (!adUsesGlobalCost) {
      return values.adCost
    }

    return values.adCostCampaign ?? values.campaignCost
  }, [values, isOffsiteTotalBudget, idIsValid, type, dataSchemaTable])

  const initialData = useMemo(
    () => ({
      budget: Number(initialCostOverride ?? costValue)
    }),
    [costValue, initialCostOverride]
  )

  const showEditSection = useMemo(
    () => isEdit && canEdit && !isPublisher(),
    [isEdit, canEdit]
  )

  return (
    <>
      {showEditSection ? (
        <Form initialData={initialData} onSubmit={handleSubmit} ref={formRef}>
          <EditCost
            minCost={values.selfMinCost}
            adCost={values.adCost}
            isValidCost={isValidCost}
            onCancel={cancelEdit}
            showMin={showMin}
            onEditRequest={handleEditRequest}
            origin={origin}
            localLoading={localLoading}
            initialCost={initialData.budget}
          />
        </Form>
      ) : (
        <ViewCost
          value={costValue}
          showMinBadge={showMinBadge}
          canEdit={canEdit}
          onRequestEdit={requestEdit}
          type={type}
        />
      )}
    </>
  )
}
